import React from 'react';
import { useTranslation } from 'react-i18next';
import { getPersistedQuantityForUnit } from '../../../utils/commercialQuantity';
import type { AllocationUnit } from '../../../utils/dimensionConversion';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit, normalizeUnitCode } from '../../../utils/units';
import './OrderQuantityPreview.css';

export interface OrderQuantityPreviewSource {
  quantity?: number | null;
  unit?: string | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  catalogProductName?: string | null;
  productCategoryName?: string | null;
}

/** The same visible equivalents drive the preview and its surrounding price caption. */
export function getOrderQuantityPreviewEquivalents(line: OrderQuantityPreviewSource) {
  // XPS is a catalog identity, not a geometry profile (ordinary boards also have three dimensions).
  const isXps = [line.catalogProductName, line.productCategoryName].some((name) => /\bXPS\b/i.test(name || ''));
  const units: AllocationUnit[] = ['pcs', isXps ? 'm3' : 'm2'];
  return units.filter((code) => code !== normalizeUnitCode(line.unit || 'pcs')).flatMap((code) => {
    const value = getPersistedQuantityForUnit({ ...line }, code);
    return value === null ? [] : [{ code, value }];
  });
}

/** Read-only physical amounts. Never infer historical equivalents from current dimensions or pricing. */
export default function OrderQuantityPreview({ line }: { line: OrderQuantityPreviewSource }) {
  const { i18n } = useTranslation();
  const unit = line.unit || 'pcs';
  const label = (value: number, code: string) => `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(code, i18n.language)}`;
  return (
    <span className="order-quantity-preview">
      <span>{line.quantity == null ? '—' : label(line.quantity, unit)}</span>
      {getOrderQuantityPreviewEquivalents(line).map(({ code, value }) => (
        <span className="order-quantity-preview__equivalent" key={code}>{label(value, code)}</span>
      ))}
    </span>
  );
}
